import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Mail, Phone, Building2, Calendar } from "lucide-react";
import { SCORED_SECTIONS, BUSINESS_PROFILE, BANDS, getBand } from "@/lib/trcaSchema";

export default function TrcaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/trca/submissions/${id}`);
        setData(data);
      } catch (ex) {
        toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      }
    })();
  }, [id]);

  const download = async () => {
    try {
      const resp = await api.get(`/trca/submissions/${id}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `trca_${(data?.company_name || "submission").replace(/\s+/g, "_")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  if (!data) return <div className="text-sm text-ink-muted uppercase tracking-wider">Loading…</div>;

  const band = getBand(data.total_score);
  const bp = data.business_profile || {};

  return (
    <div className="space-y-6" data-testid="trca-detail-page">
      <button onClick={() => nav(-1)} className="text-[12px] uppercase tracking-wider text-ink-muted hover:text-ink flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to submissions
      </button>

      {/* Header */}
      <div className="border-b border-ink-line pb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">TRCA Submission</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">{data.company_name}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-ink-muted">
            <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {data.name}</span>
            <a href={`mailto:${data.email}`} className="flex items-center gap-1.5 hover:text-brand font-num"><Mail className="w-3.5 h-3.5" /> {data.email}</a>
            <a href={`tel:${data.phone}`} className="flex items-center gap-1.5 hover:text-brand font-num"><Phone className="w-3.5 h-3.5" /> {data.phone}</a>
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(data.submitted_at).toLocaleString()}</span>
          </div>
        </div>
        <button onClick={download} data-testid="trca-detail-export" className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2.5 text-sm font-medium rounded-sm transition-colors">
          <Download className="w-4 h-4" /> Download CSV
        </button>
      </div>

      {/* Score + band */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-flat p-6 flex flex-col items-center justify-center" data-testid="detail-total-score">
          <div className="stat-label">Total score</div>
          <div className="font-num text-6xl font-bold mt-3" style={{ color: band.color }}>{data.total_score}</div>
          <div className="text-[11px] uppercase tracking-widest text-ink-muted mt-2">/ 100</div>
        </div>
        <div className="card-flat p-6 lg:col-span-2">
          <div className="stat-label">Readiness band</div>
          <div className="font-display text-3xl font-bold tracking-tight mt-2" style={{ color: band.color }}>{band.name}</div>
          <p className="text-sm text-ink leading-relaxed mt-2">{band.desc}</p>
          <div className="mt-5 space-y-2">
            {SCORED_SECTIONS.map((s) => {
              const score = data.section_scores?.[s.key] ?? 0;
              const pct = (score / s.max) * 100;
              return (
                <div key={s.key}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-ink">{s.title}</span>
                    <span className="font-num font-bold">{score}<span className="text-ink-muted font-normal"> / {s.max}</span></span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-sm overflow-hidden"><div className="h-full bg-brand" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Business profile */}
      <div className="card-flat" data-testid="detail-business-profile">
        <div className="px-6 py-4 border-b border-ink-line">
          <div className="stat-label">Business profile</div>
          <div className="font-display font-bold text-lg tracking-tight mt-1">Context for advisory</div>
        </div>
        <div className="divide-y divide-ink-line">
          {Object.entries(BUSINESS_PROFILE).map(([k, meta]) => {
            let val = bp[k];
            if (Array.isArray(val)) val = val.length ? val.join(" · ") : "—";
            if (!val) val = "—";
            return (
              <div key={k} className="px-6 py-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="text-xs uppercase tracking-wider text-ink-muted sm:col-span-1">{meta.label}</div>
                <div className="text-sm sm:col-span-2">{val}</div>
              </div>
            );
          })}
          {bp.industries_other && (
            <div className="px-6 py-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="text-xs uppercase tracking-wider text-ink-muted">Industries (Other)</div>
              <div className="text-sm sm:col-span-2">{bp.industries_other}</div>
            </div>
          )}
        </div>
      </div>

      {/* Answers per section */}
      <div className="space-y-4">
        {SCORED_SECTIONS.map((s) => {
          const score = data.section_scores?.[s.key] ?? 0;
          return (
            <div key={s.key} className="card-flat" data-testid={`detail-section-${s.key}`}>
              <div className="px-6 py-4 border-b border-ink-line flex items-center justify-between">
                <div>
                  <div className="stat-label">{`Section ${SCORED_SECTIONS.indexOf(s) + 3}`}</div>
                  <div className="font-display font-bold text-lg tracking-tight mt-1">{s.title}</div>
                </div>
                <div className="chip border-brand/30 bg-brand-soft text-brand font-num">{score} / {s.max}</div>
              </div>
              <div className="divide-y divide-ink-line">
                {s.questions.map((q) => {
                  const picks = data.answers?.[q.key] || [];
                  return (
                    <div key={q.key} className="px-6 py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-num text-[11px] text-ink-muted uppercase tracking-wider">{q.key.toUpperCase()}</div>
                          <div className="font-medium text-sm mt-0.5">{q.label}</div>
                        </div>
                        <div className="font-num text-xs text-ink-muted shrink-0">{picks.length} / {q.options.length}</div>
                      </div>
                      {picks.length === 0 ? (
                        <div className="mt-2 text-xs italic text-ink-muted">No options selected</div>
                      ) : (
                        <ul className="mt-2 space-y-1">
                          {picks.map((p) => (
                            <li key={p} className="text-sm flex items-start gap-2">
                              <span className="text-brand mt-1 w-1 h-1 rounded-full bg-brand shrink-0" /> {p}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
